import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import DevisCopier from '@/components/DevisCopier';
import DevisGenerator from '@/components/DevisGenerator';
import {
  Copy, FileText, PenLine, ArrowLeft, Loader2, Sparkles, X,
} from 'lucide-react';

/**
 * Modal that opens when clicking "Nouveau Devis" — replaces the previous
 * minimalist DevisForm popup. Lets the user choose between :
 *   1. IA Copier (upload competitor quote)
 *   2. IA Libre  (description text/voice → AI generates lines)
 *   3. Création manuelle (creates a blank devis and redirects to the full
 *      detail page where chantier name, address and désignations are filled in)
 */
export default function NewDevisDialog({ open, onOpenChange, onDevisCreated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | 'copier' | 'libre' | 'legacy'
  const [creating, setCreating] = useState(false);

  const reset = () => {
    setMode(null);
    setCreating(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleDevisDone = () => {
    reset();
    onOpenChange(false);
    if (onDevisCreated) onDevisCreated();
  };

  // "Création manuelle" → create a stub devis then jump straight into the
  // existing rich DevisDetail page (chantier name, address, désignations…).
  const startManual = async () => {
    setCreating(true);
    try {
      const r = await api.post('/devis', {
        statut: 'Brouillon',
        type_prestation: 'BTP',
        montant_estime: 0,
      });
      const id = r.data.devis_id;
      close();
      navigate(`/devis/${id}`);
      toast.success('Devis créé — complétez les informations');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            {mode && (
              <button
                onClick={reset}
                className="p-1.5 -ml-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
                data-testid="new-devis-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {!mode && 'Nouveau devis'}
                {mode === 'copier' && 'IA · Copier un devis concurrent'}
                {mode === 'legacy' && 'Importer un devis existant'}
                {mode === 'libre' && 'IA · Génération libre'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {!mode && 'Choisissez la méthode de création'}
                {mode === 'copier' && 'Téléversez le PDF ou la photo, on extrait et adapte les prix'}
                {mode === 'legacy' && 'Téléversez votre ancien devis PDF/photo — l\'IA extrait les lignes et crée la fiche dans le CRM'}
                {mode === 'libre' && 'Décrivez le chantier à voix ou par écrit'}
              </p>
            </div>
          </div>
          <button
            onClick={close}
            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
            data-testid="new-devis-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* CHOOSER */}
          {!mode && (
            <div className="space-y-4" data-testid="new-devis-chooser">
              <p className="text-sm text-slate-600">
                <Sparkles className="w-4 h-4 inline mr-1 text-purple-500" />
                Trois manières de créer un devis selon votre besoin :
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Tile 1 — IA Copier */}
                <button
                  onClick={() => setMode('copier')}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 hover:border-purple-400 hover:shadow-lg transition-all text-left bg-white"
                  data-testid="tile-ia-copier"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-100 group-hover:bg-purple-200 flex items-center justify-center mb-3 transition-colors">
                    <Copy className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">IA · Copier</h3>
                  <p className="text-xs text-purple-600 font-medium mb-2">Devis concurrent</p>
                  <p className="text-sm text-slate-600 leading-snug">
                    Uploadez le PDF ou la photo d&apos;un devis concurrent.
                    L&apos;IA extrait toutes les lignes, vous ajustez les prix.
                  </p>
                </button>

                {/* Tile 2 — IA Libre */}
                <button
                  onClick={() => setMode('libre')}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all text-left bg-white"
                  data-testid="tile-ia-libre"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-3 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">IA · Libre</h3>
                  <p className="text-xs text-blue-600 font-medium mb-2">Description texte ou vocal</p>
                  <p className="text-sm text-slate-600 leading-snug">
                    Décrivez le chantier à l&apos;oral ou par écrit.
                    L&apos;IA génère automatiquement les lignes avec les prix du marché.
                  </p>
                </button>

                {/* Tile 3 — Création manuelle */}
                <button
                  onClick={startManual}
                  disabled={creating}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all text-left bg-white disabled:opacity-60"
                  data-testid="tile-manuel"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-3 transition-colors">
                    {creating
                      ? <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                      : <PenLine className="w-6 h-6 text-emerald-600" />}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">Création manuelle</h3>
                  <p className="text-xs text-emerald-600 font-medium mb-2">Saisie complète</p>
                  <p className="text-sm text-slate-600 leading-snug">
                    Ouvre la page de devis complète : nom du projet, adresse du chantier,
                    désignations, photos, analyse de rentabilité…
                  </p>
                </button>
              </div>

              {/* Import legacy PDF — 4e option en pleine largeur (pour devis pré-CRM) */}
              <button
                onClick={() => setMode('legacy')}
                className="w-full mt-2 p-4 rounded-xl border-2 border-dashed border-amber-300 hover:border-amber-500 hover:bg-amber-50/40 transition-all text-left"
                data-testid="tile-import-legacy"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 text-sm">Importer un devis existant (PDF ou photo)</h3>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Vous avez un devis fait avant le CRM ? Uploadez son PDF, l&apos;IA extrait les lignes,
                      vous validez et le devis apparaît dans votre liste (statut modifiable ensuite en « Accepté »).
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* MODE: IA COPIER (devis concurrent) */}
          {mode === 'copier' && (
            <div className="bg-white rounded-xl">
              <DevisCopier onDevisCreated={handleDevisDone} mode="competitor" />
            </div>
          )}

          {/* MODE: LEGACY (mon ancien devis à réimporter) */}
          {mode === 'legacy' && (
            <div className="bg-white rounded-xl">
              <DevisCopier onDevisCreated={handleDevisDone} mode="legacy" />
            </div>
          )}

          {/* MODE: IA LIBRE */}
          {mode === 'libre' && (
            <div className="bg-white rounded-xl">
              <DevisGenerator onDevisCreated={handleDevisDone} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
